import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { waLink } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Inbox, CheckCircle2, PhoneCall, XCircle, Trash2, MessageCircle } from "lucide-react";

const STATUS = {
  new: { label: "جديد", cls: "bg-amber-100 text-amber-800" },
  contacted: { label: "تم التواصل", cls: "bg-sky-100 text-sky-800" },
  accepted: { label: "مقبول", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "مرفوض", cls: "bg-red-100 text-red-700" },
};

export default function Trials() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trial-requests");
      setRows(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/trial-requests/${id}`, { status });
      if (status === "accepted")
        toast.success(data.created_school_id ? "تم القبول وإنشاء المدرسة (تجريبي 14 يوم). أضف المرشد من صفحة المرشدين." : "تم القبول");
      else toast.success("تم تحديث حالة الطلب");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/trial-requests/${id}`);
      toast.success("تم حذف الطلب");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const newCount = rows.filter((r) => r.status === "new").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-800">
            <Inbox className="h-6 w-6 text-emerald-700" /> طلبات التجربة
          </h1>
          <p className="text-slate-400">طلبات النسخة التجريبية الواردة من صفحة الدخول</p>
        </div>
        {newCount > 0 && <Badge className="bg-amber-100 px-3 py-1.5 text-amber-800" data-testid="new-trials-count">{newCount} طلب جديد</Badge>}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
              <Inbox className="h-10 w-10" />
              <p>لا توجد طلبات تجربة حتى الآن</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">المدرسة</TableHead>
                  <TableHead className="text-right">المرشد</TableHead>
                  <TableHead className="text-right">الجوال</TableHead>
                  <TableHead className="text-right">الطلاب</TableHead>
                  <TableHead className="text-right">المدينة</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-center">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`trial-row-${r.id}`}>
                    <TableCell className="font-semibold text-slate-800">{r.school_name}</TableCell>
                    <TableCell>{r.counselor_name}</TableCell>
                    <TableCell dir="ltr" className="text-right font-mono text-sm">{r.phone}</TableCell>
                    <TableCell>{r.students_count || "—"}</TableCell>
                    <TableCell>{r.city || "—"}</TableCell>
                    <TableCell>
                      <Badge className={STATUS[r.status]?.cls}>{STATUS[r.status]?.label || r.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap justify-center gap-1">
                        <a href={waLink(`السلام عليكم ${r.counselor_name}، بخصوص طلب تجربة منصة معاكم لمدرسة ${r.school_name}`)}
                          target="_blank" rel="noopener noreferrer" title="تواصل عبر واتساب">
                          <Button variant="ghost" size="icon" data-testid={`trial-wa-${r.id}`}>
                            <MessageCircle className="h-4 w-4 text-emerald-600" />
                          </Button>
                        </a>
                        {r.status !== "accepted" && (
                          <Button variant="ghost" size="icon" title="قبول وإنشاء مدرسة"
                            onClick={() => setStatus(r.id, "accepted")} data-testid={`trial-accept-${r.id}`}>
                            <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                          </Button>
                        )}
                        {r.status !== "contacted" && (
                          <Button variant="ghost" size="icon" title="تم التواصل"
                            onClick={() => setStatus(r.id, "contacted")} data-testid={`trial-contacted-${r.id}`}>
                            <PhoneCall className="h-4 w-4 text-sky-600" />
                          </Button>
                        )}
                        {r.status !== "rejected" && (
                          <Button variant="ghost" size="icon" title="رفض"
                            onClick={() => setStatus(r.id, "rejected")} data-testid={`trial-reject-${r.id}`}>
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" title="حذف" data-testid={`trial-delete-${r.id}`}>
                              <Trash2 className="h-4 w-4 text-slate-400" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="text-right">
                            <AlertDialogHeader>
                              <AlertDialogTitle>حذف الطلب</AlertDialogTitle>
                              <AlertDialogDescription>هل تريد حذف طلب «{r.school_name}»؟</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>إلغاء</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(r.id)} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
